import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Table,
} from "@mui/material";
import { Autorenew, People } from "@mui/icons-material";
import { IRaffle } from "../../../store/interfaces/IRaffle";
import {
  useGetLastActiveRaffleQuery,
  useGetPurchaseOrdersByStatusQuery,
} from "../../../store/api";
import { ArrowDown } from "lucide-react";
import { getThemeByAccountId } from "../../../utils/styleConstants";

interface RemainingProps {
  raffle: IRaffle;
}

export const Remaining = ({ raffle }: RemainingProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const theme = useTheme();

  const {
    data: lastActiveRaffleFounded,
  } = useGetLastActiveRaffleQuery(raffle.accountId, {
    skip: raffle.accountId === "default",
  });

  const { data: purchaseOrdersByStatus } = useGetPurchaseOrdersByStatusQuery(
    {
      raffleId: lastActiveRaffleFounded?.id || "",
      purchaseOrderStatus: "TRANSFER_PAYMENT_CREATED",
    },
    {
      skip: !lastActiveRaffleFounded,
    }
  );

  const handleOpenDialog = () => {
    if (lastActiveRaffleFounded?.id === raffle.id) {
      alert("❌ No puedes transferir órdenes a la misma rifa.");
      return;
    }
    setOpenDialog(true);
  };

  const orderCount = purchaseOrdersByStatus?.length || 0;

  const stats = useMemo(() => {
    let totalTickets = 0;
    let totalCLP = 0;
    let totalUSD = 0;

    purchaseOrdersByStatus?.forEach((order) => {
      totalTickets += order.ticketQuantity;
      totalCLP += order.totalAmount;
      totalUSD += order.totalAmountUSD;
    });

    return { totalTickets, totalCLP, totalUSD };
  }, [purchaseOrdersByStatus]);

  const handleTransferOrders = () => {
    console.log(`Moviendo ${orderCount} órdenes / ${stats.totalTickets} tickets`);
    setOpenDialog(false);
  };

  const showPremiumAlert = !raffle.isPremium;

  const cellStyle = {
    padding: "8px 12px",
    border: "1px solid",
    textAlign: "left",
    fontSize: "0.9rem",
  };

  return (
    <Card
      sx={{
        mx: "auto",
        p: 3,
        borderRadius: 3,
        boxShadow: 4,
        maxWidth: 900,
        textAlign: "center",
      }}
    >

<CardContent
  sx={{
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    overflow: "hidden",
  }}
>
  {showPremiumAlert && (
    <>
      {/* Overlay Blur */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(2px)",
          zIndex: 1,
        }}
      />

      {/* Alert Message */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 2,
          backgroundColor: getThemeByAccountId(raffle.accountId) === "dark" ? "black" : "white",
          border: `2px solid white`,
          borderRadius: 2,
          padding: 4,
          boxShadow: 6,
          textAlign: "center",
          maxWidth: 400,
        }}
      >
        <Typography variant="h6" color="warning.main" gutterBottom>
          🚫 Funcionalidad Premium
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Esta funcionalidad permite <strong>transferir órdenes de compra activas</strong> desde una rifa anterior hacia esta rifa nueva, conservando los tickets y montos asociados.
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={2}>
          Es ideal cuando quieres consolidar ventas entre rifas o relanzar una campaña sin perder a los compradores anteriores. Disponible solo para rifas con plan Premium.
        </Typography>

      </Box>
    </>
  )}

        <Typography variant="h5" color="primary" gutterBottom>
          🛠 Órdenes listas para transferirse
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Hemos encontrado <strong>{orderCount}</strong> órdenes de compra de la rifa{" "}
          <strong>{lastActiveRaffleFounded?.name}</strong> (ÚLTIMA RIFA).
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Estas se transferirán a{" "}
          <strong>{raffle.name}</strong> con un total aproximado de:
        </Typography>
        <Grid container spacing={2} justifyContent="center" mb={4}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6">🎟 Tickets</Typography>
              <Typography variant="body1" fontWeight="bold">
                {stats.totalTickets}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6">💰 Monto CLP</Typography>
              <Typography variant="body1" fontWeight="bold">
                ${stats.totalCLP.toLocaleString()}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6">💵 Monto USD</Typography>
              <Typography variant="body1" fontWeight="bold">
                ${stats.totalUSD.toFixed(2)}
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {orderCount > 0 && (
          <Box sx={{ width: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Detalle de órdenes encontradas:
            </Typography>
            <TableContainer sx={{ maxHeight: "60vh" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Tickets</TableCell>
                    <TableCell align="right">CLP</TableCell>
                    <TableCell align="right">USD</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseOrdersByStatus?.map((order, index) => (
                    <TableRow key={order.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{order.purchaser.name}</TableCell>
                      <TableCell>{order.purchaser.email}</TableCell>
                      <TableCell align="right">{order.ticketQuantity}</TableCell>
                      <TableCell align="right">${order.totalAmount.toLocaleString()}</TableCell>
                      <TableCell align="right">${order.totalAmountUSD.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}



        {/* Flecha animada */}
        <Box sx={{ fontSize: "2rem", my: 2, position: "relative", height: "2.5rem" }}>
          
            <Typography fontWeight={"bold"}> {lastActiveRaffleFounded?.name} </Typography>
            <Typography sx={{
              animation: "arrowMove 1s infinite ease-in-out",
            }}> <ArrowDown></ArrowDown>  </Typography>
            <Typography fontWeight={"bold"} mt={1}> {raffle.name} </Typography>
             
        </Box>

        <Button
          variant="contained"
          color="primary"
          disabled={!raffle.isPremium}
          onClick={handleOpenDialog}
          sx={{ mt: 6, px: 4, py: 1.5, fontSize: "1rem", fontWeight: "bold" }}
        >
          Mover órdenes de compra
        </Button>

        {raffle.isPremium ? <Typography variant="body2" sx={{ mt: 3, color: "gray" }}>
          ✅ Esta funcionalidad ya está disponible y funcionando.
        </Typography> : <Typography variant="body2" sx={{ mt: 3, color: "gray" }}>
          🚫 Esta funcionalidad no esta disponible para este tipo de rifa.
        </Typography>}
        
      </CardContent>

      {/* Diálogo de Confirmación */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>¿Confirmar transferencia?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se moverán <strong>{orderCount}</strong> órdenes con un total de{" "}
            <strong>{stats.totalTickets} tickets</strong> desde{" "}
            <strong>{lastActiveRaffleFounded?.name}</strong> a{" "}
            <strong>{raffle.name}</strong>. ¿Deseas continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleTransferOrders} color="primary" variant="contained">
            Confirmar y Mover
          </Button>
        </DialogActions>
      </Dialog>

      <style>
        {`
          @keyframes arrowMove {
            0% { transform: translateX(0); opacity: 0.5; }
            50% { transform: translateY(10px); opacity: 1; }
            100% { transform: translateX(0); opacity: 0.5; }
          }
        `}
      </style>
    </Card>
  );
};
